/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Context } from '@squiz/dxp-ai-client';
import { PERMISSIONS } from '@squiz/dxp-auth';
import { Button, ICON_SEARCH, SearchFilter, SearchFilterProps } from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { DXP_SCREEN_STRUCTURE_STANDARD } from 'apps/console-core/src/Components/DxpScreen/types/DxpScreenStructure.types';
import { LoadingScreenEmpty } from 'apps/console-core/src/Screens/LoadingScreenEmpty/LoadingScreenEmpty';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AiBreadcrumbs } from '../../Components/AiBreadcrumbs/AiBreadcrumbs';
import { useContextItemDialog } from '../../Components/AiContextItemDialog/useContextItemDialog';
import { AiContextItemTable } from '../../Components/AiContextItemTable/AiContextItemTable';
import { VerticalDivider } from '../../Components/VerticalDivider/VerticalDivider';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { useCache, useContextSchema, useGetAi } from '../../Services/ai/src/Hooks';

export const AiViewChatbotContext = (): JSX.Element => {
  const params = useParams();
  const aiId = params.aiId as string;
  const contextId = params.contextId as string;
  const [ai, aiError, aiState] = useGetAi(aiId, 'chatbot');
  const [contextSchema, contextSchemaError, contextSchemaState] = useContextSchema(aiId, contextId);
  const isLoading = aiState === 'pending' || contextSchemaState === 'pending';
  const isError = aiState === 'error' || contextSchemaState === 'error';
  const { cacheKey, clearCache } = useCache();
  const contextItemDialog = useContextItemDialog();
  const { t } = useTranslation();
  const context: Context | null = contextSchema && {
    description: '',
    id: contextId,
    name: String(contextSchema.title),
  };

  const labels = {
    contextItemsEmpty: t(AI_STRINGS.contextsEmpty),
    createContextItem: t(AI_STRINGS.createContextItem),
    heading: t(`Manage context "${context?.name}"`),
  };

  const [filterValue, setFilterValue] = useState<string>(``);

  const filterProps = {
    customIcon: ICON_SEARCH,
    filterValue,
    placeholder: `Search context items`,
    setFilterValue,
  } as SearchFilterProps;

  if (isLoading) {
    return <LoadingScreenEmpty></LoadingScreenEmpty>;
  }

  return (
    <DxpScreen
      breadcrumbs={ai && context ? <AiBreadcrumbs ai={ai} context={context} /> : undefined}
      heading={contextSchema ? `Manage context "${contextSchema.title}"` : ``}
      showSeparator={!isError}
      structure={DXP_SCREEN_STRUCTURE_STANDARD}
    >
      {isError && <div>{String(aiError || contextSchemaError)}</div>}
      {!isError && hasPermission(PERMISSIONS.dxpAi.CONTEXT_INSTANCE_READ) && ai && contextSchema && (
        <>
          <div className="ai_context-items-filters">
            <div className="ai_context-items-filters-inputs">
              <SearchFilter {...filterProps} />
            </div>
            {hasPermission(PERMISSIONS.dxpAi.CONTEXT_INSTANCE_WRITE) && (
              <>
                <VerticalDivider />
                <div>
                  <Button
                    data-id="Create Context Item Button"
                    onClick={() => contextItemDialog.open({ ai, clearCache, contextId, schema: contextSchema })}
                    text={labels.createContextItem}
                  />
                </div>
              </>
            )}
          </div>
          <AiContextItemTable
            ai={ai}
            cacheKey={cacheKey}
            clearCache={clearCache}
            contextId={contextId}
            contextSchema={contextSchema}
            filter={filterValue}
            labels={{
              emptyText: labels.contextItemsEmpty,
              heading: labels.heading,
            }}
            maxColumns={4}
          />
        </>
      )}
    </DxpScreen>
  );
};
